import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { UserDto, CreateEventDto, EventDto, EventContentDto, ReferentDto } from "@api";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { AppRoutes, RouteParams } from "src/app/constants";
import { RootState } from "src/app/store";
import * as EventActions from "src/app/store/event/actions";
import * as FromUser from "src/app/store/user/selectors";
import * as FromEvent from "src/app/store/event/selectors";
import { NavController } from "@ionic/angular";
import { ActivatedRoute } from "@angular/router";
import { filter } from "rxjs/operators";
import { differenceInMinutes } from "date-fns";

@Component({
  selector: "app-event-form",
  templateUrl: "./event-form.page.html",
  styleUrls: ["./event-form.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventFormPage implements OnInit, OnDestroy {
  public routes: typeof AppRoutes = AppRoutes;
  public form: FormGroup;
  public minDate = new Date();
  public contentId?: string;
  public eventId?: string;
  public showDatetime: boolean;
  public content$: Observable<EventContentDto> = this.store.select(FromEvent.selectContent);
  public event$: Observable<EventDto> = this.store.select(FromEvent.selectEvent);
  public loading$: Observable<boolean> = this.store.select(FromEvent.selectLoading);
  public user$: Observable<UserDto> = this.store.select(FromUser.selectUser);
  public referents$: Observable<ReferentDto[]> = this.store.select(FromEvent.selectReferents);
  public breadcrumbs = [
    { name: "Dashboard", link: `/${this.routes.home}` },
    { name: "Veranstaltungen verwalten", link: `/${this.routes.eventContents}` },
    { name: "Veranstaltung datum" },
  ];

  private $content: Subscription;
  private $event: Subscription;

  constructor(private activatedRoute: ActivatedRoute, private store: Store<RootState>, private navController: NavController) {}

  public ngOnInit(): void {
    this.contentId = this.activatedRoute.snapshot.paramMap.get(RouteParams.contentId);
    this.eventId = this.activatedRoute.snapshot.paramMap.get(RouteParams.eventId);

    this.form = new FormGroup({
      date: new FormControl(null, [Validators.required]),
      closingDate: new FormControl(null, [Validators.required]),
      referentId: new FormControl(null, [Validators.required]),
      location: new FormControl(null, [Validators.required]),
    });

    this.$content = this.content$.pipe(filter(content => !!content)).subscribe(content => {
      this.breadcrumbs = [
        { name: "Dashboard", link: `/${AppRoutes.home}` },
        { name: "Veranstaltungen verwalten", link: `/${AppRoutes.eventContents}` },
        { name: content.title, link: `/${AppRoutes.eventContents}/${content.id}` },
        { name: "Veranstaltung datum" },
      ];
    });

    if (this.eventId) {
      this.$event = this.event$.pipe(filter(event => !!event)).subscribe(event => {
        const minutesDiff = differenceInMinutes(new Date(event.date), new Date(event.closingDate));
        this.form.patchValue({ ...event, closingDate: minutesDiff });
      });
    }
  }

  public ngOnDestroy(): void {
    this.$content?.unsubscribe();
    this.$event?.unsubscribe();
  }

  public cancel(): void {
    this.navController.back();
  }

  public async save(): Promise<void> {
    const { date, closingDate } = this.form.value;

    const eventDate = new Date(date);
    eventDate.setSeconds(0);
    eventDate.setMilliseconds(0);

    const eventClosingDate = new Date(eventDate);
    eventClosingDate.setMinutes(eventClosingDate.getMinutes() - closingDate);

    const payload: CreateEventDto = {
      ...this.form.value,
      contentId: this.contentId,
      date: eventDate,
      closingDate: eventClosingDate,
    };

    if (this.eventId) this.update(payload);
    else this.create(payload);
  }

  protected ionViewDidEnter(): void {
    this.store.dispatch(EventActions.getEventContent({ contentId: this.contentId }));
    this.store.dispatch(EventActions.getReferents());

    if (this.eventId) {
      this.store.dispatch(EventActions.getEvent({ eventId: this.eventId }));
    }
  }

  private create(createEventDto: CreateEventDto): void {
    this.store.dispatch(EventActions.createEvent({ createEventDto }));
  }

  private update(updateEventDto: CreateEventDto): void {
    this.store.dispatch(EventActions.updateEvent({ eventId: this.eventId, updateEventDto }));
  }
}
